import { apiRequest } from "../../api/apiClient";
import type {
  Applicant,
  CreateApplicantDto,
  UpdateApplicantDto,
} from "../../types/applicant.types";

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.message ||
        error.title ||
        `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return {} as T;
  }

  return response.json();
};

export const applicantService = {
  /**
   * Get all divorce applicants
   */
  getDivorceApplicants: async (): Promise<Applicant[]> => {
    const response = await apiRequest(`/DivorceApplicants`, {
      method: "GET",
    });
    return handleResponse<Applicant[]>(response);
  },

  /**
   * Get applicants by certificate application ID
   */
  getApplicantsByCertificateId: async (
    certificateApplicationId: number,
  ): Promise<Applicant[]> => {
    const response = await apiRequest(
      `/Applicants/by-certificate/${certificateApplicationId}`,
      {
        method: "GET",
      },
    );
    return handleResponse<Applicant[]>(response);
  },

  /**
   * Get divorce applicants by certificate application ID
   */
  getDivorceApplicantsByCertificateId: async (
    certificateApplicationId: number,
  ): Promise<Applicant[]> => {
    const response = await apiRequest(
      `/DivorceApplicants/by-certificate/${certificateApplicationId}`,
      {
        method: "GET",
      },
    );
    return handleResponse<Applicant[]>(response);
  },

  /**
   * Get a single applicant by ID
   */
  getApplicantById: async (id: number): Promise<Applicant> => {
    const response = await apiRequest(`/Applicants/${id}`, {
      method: "GET",
    });
    return handleResponse<Applicant>(response);
  },

  /**
   * Create a new applicant
   */
  createApplicant: async (
    data: CreateApplicantDto,
    photo?: File,
  ): Promise<Applicant> => {
    const formData = new FormData();

    // Append all data fields to FormData
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null && key !== "profileImagePath") {
        formData.append(key, value.toString());
      }
    });

    // Append photo file if provided
    if (photo) {
      formData.append("photo", photo);
    }

    const response = await apiRequest("/Applicants", {
      method: "POST",
      body: formData,
    });
    return handleResponse<Applicant>(response);
  },

  /**
   * Create a new divorce applicant
   */
  createDivorceApplicant: async (
    data: CreateApplicantDto,
    photo?: File,
  ): Promise<Applicant> => {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null && key !== "profileImagePath") {
        formData.append(key, value.toString());
      }
    });

    if (photo) {
      formData.append("photo", photo);
    }

    const response = await apiRequest("/DivorceApplicants", {
      method: "POST",
      body: formData,
    });
    return handleResponse<Applicant>(response);
  },

  /**
   * Update an existing applicant
   */
  updateApplicant: async (
    id: number,
    data: UpdateApplicantDto,
    photo?: File,
  ): Promise<Applicant> => {
    const formData = new FormData();

    // Append all data fields to FormData
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null && key !== "profileImagePath") {
        formData.append(key, value.toString());
      }
    });

    // Append photo file if provided
    if (photo) {
      formData.append("photo", photo);
    }

    const response = await apiRequest(`/Applicants/${id}`, {
      method: "PUT",
      body: formData,
    });
    return handleResponse<Applicant>(response);
  },

  /**
   * Update an existing divorce applicant
   */
  updateDivorceApplicant: async (
    id: number,
    data: UpdateApplicantDto,
    photo?: File,
  ): Promise<Applicant> => {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null && key !== "profileImagePath") {
        formData.append(key, value.toString());
      }
    });

    if (photo) {
      formData.append("photo", photo);
    }

    const response = await apiRequest(`/DivorceApplicants/${id}`, {
      method: "PUT",
      body: formData,
    });
    return handleResponse<Applicant>(response);
  },

  /**
   * Delete an applicant
   */
  deleteApplicant: async (id: number): Promise<void> => {
    const response = await apiRequest(`/Applicants/${id}`, {
      method: "DELETE",
    });
    await handleResponse<void>(response);
  },
};
